import { Dispatch, useEffect, useMemo, useState } from "react";
import { Button } from "reactstrap";
import { SelectPicker } from "rsuite";

import AppDrawer from "../../Drawers/AppDrawer";
import AppSpinner from "../../../elements/AppSpinner";
import { sanitizeWidget } from "../../../utils/DashboardUtils";
import WidgetRenderer from "../WidgetRenderer";
import NameDescriptionGroup from "./NameDescriptionGroup";
import Constants from "../../../utils/Constants";
import SettingsGroup from "../../CRM/SettingsGroup";
import ModulePicker from "../../Modules/ModulePicker";
import RequiredTitle from "../../../elements/RequiredTitle";
import useModule from "../../../hooks/useModule";
import { Field, Module } from "../../../types/TenantInterfaces";
import ModuleFieldsPicker from "../../TableLayouts/ModuleFieldsPicker";

interface MapSettingsProps {
  open: boolean;
  onClose: () => void;
  setSettings: Dispatch<any>;
  section: any;
  updateWidget: (newSection: any, onSuccess: any) => void;
  theme: string | undefined;
  insightsModule: string | undefined;
  isUpdating: boolean;
  record: Record<string, any> | undefined;
  topLevelFilters?: any;
}

const InitialSection = (section: any, insightsModule?: string) => {
  return {
    ModuleAPIName: insightsModule ?? "",
    Query: {
      Fields: [] as string[],
      Filters: [] as any[],
      Lookup: [] as any[],
      Pagination: {
        Count: 100,
        Page: 1,
      },
    },
    ...section,
  };
};

/**
 * A widget which displays markers on a map.
 *
 */
const MapSettings = ({
  open,
  onClose,
  section,
  updateWidget,
  theme,
  isUpdating,
  record,
  insightsModule,
  topLevelFilters,
}: MapSettingsProps) => {
  const [widget, setWidget] = useState(
    JSON.parse(JSON.stringify(InitialSection(section, insightsModule)))
  );
  const { module, getModule } = useModule(
    widget?.ModuleAPIName || insightsModule
  );

  useEffect(() => {
    setWidget(
      JSON.parse(JSON.stringify(InitialSection(section, insightsModule)))
    );
  }, [section]);

  const locationFields = useMemo(() => {
    if (!module?.Fields) return [];

    return module?.Fields?.flatMap((f: any) => {
      if (f.FieldType !== "Location") return [];

      return [{ label: f.Name, value: f.APIName }];
    });
  }, [module, insightsModule]);

  const handleClose = () => {
    onClose();
    setWidget(null);
  };

  const handleSave = () => {
    if (JSON.stringify(widget) === JSON.stringify(section)) {
      return handleClose();
    }

    sanitizeWidget(widget);

    widget.Query = {
      ...widget.Query,
      Filters: [
        {
          Group: true,
          Filters: [
            {
              Field: widget?.LocationField,
              Method: "is-not-empty",
              Value: "",
            },
          ],
        },
      ],
      Pagination: {
        Count: 100,
        Page: 1,
      },
    };

    updateWidget(widget, handleClose);
  };

  const updateSection = (prop: any, value: any) => {
    setWidget((prev: any) => {
      if (!prev) return null;
      prev[prop] = value;
      return { ...prev };
    });
  };

  return (
    <AppDrawer
      size="full"
      open={open}
      onClose={handleClose}
      placement="bottom"
      headerTitle={`${widget?.Name} Settings`}
      bodyStyle={{ padding: 0 }}
      headerOptions={
        <Button
          color="primary"
          size="sm"
          onClick={handleSave}
          style={{ width: 100 }}
          disabled={
            isUpdating ||
            !widget?.ModuleAPIName ||
            !widget?.LocationField ||
            !widget?.Query?.Fields?.length
          }
        >
          {isUpdating ? <AppSpinner color="white" /> : "Save"}
        </Button>
      }
      body={
        <div className="d-flex" style={{ height: "100%" }}>
          <div
            style={{
              width: "calc(100% - 350px)",
            }}
            className="py-4"
          >
            {/* @ts-ignore */}
            <WidgetRenderer
              section={widget}
              setSettings={null}
              record={record}
              theme={theme}
              topLevelFilters={topLevelFilters}
            />
          </div>
          {/* Settings of the widget. */}
          <div
            style={{ minWidth: 350, overflowY: "auto", maxWidth: 350 }}
            className="border-left p-4"
          >
            <NameDescriptionGroup
              widget={widget}
              updateSection={updateSection}
              theme={Constants.DASHBOARD_COLOR_THEMES?.[theme!]}
            />
            {/** Data Source Settings */}
            <MapDataSourceSettings
              widget={widget}
              insightsModule={insightsModule}
              updateSection={updateSection}
              locationFields={locationFields}
              module={module}
            />
            {/** Pin Size Data Source Settings  */}
            <MapPinSizeDataSourceSettings
              widget={widget}
              updateSection={updateSection}
              getModule={getModule}
            />
            {/** Clustering Settings */}
          </div>
        </div>
      }
    />
  );
};

function MapDataSourceSettings({
  widget,
  insightsModule,
  updateSection,
  locationFields,
  module,
}: {
  widget: any;
  insightsModule: string | undefined;
  updateSection: (propName: string, value: any) => void;
  locationFields: Field[];
  module: Module | null;
}) {
  const availableFields = useMemo(() => {
    if (!module?.Fields?.length) return [];

    return module?.Fields?.filter(
      (f) => !["Subitem", "MultiSubitem"].includes(f.FieldType)
    );
  }, [module]);

  const updateDataSource = (newModule: string) => {
    updateSection("ModuleAPIName", newModule);
  };

  const updateQueryFields = (newFields: string[]) => {
    let query = widget?.Query;

    if (!query) {
      query = {
        Fields: [],
        Filters: [],
        Lookup: [] as any[],
      };
    }

    query.Fields = newFields;

    updateSection("Query", query);
  };

  return (
    <>
      <SettingsGroup
        title="Data Source"
        className="mb-2"
        externalTrigger={null}
        closeExternalTrigger={null}
        disabledComponent={false}
      >
        <>
          {/** Section to select the origin module to retrieve the data. Only avaiable for Dashboards views. */}
          {!insightsModule && (
            <div className="pb-4">
              <RequiredTitle title="Origin Module" size="sm" titleCSS="mb-0" />
              <h5
                className="font-weight-500 mb-2 mt-0"
                style={{ display: "inline-block" }}
              >
                The module where to retrieve the data from.
              </h5>
              <ModulePicker
                value={widget?.ModuleAPIName}
                onChange={updateDataSource}
                oneModule={insightsModule}
              />
            </div>
          )}

          {!!module ? (
            <>
              {/** Location field section */}
              <div className="pb-4">
                <RequiredTitle
                  title="Location Field"
                  size="sm"
                  titleCSS="mb-0"
                />
                <h5
                  className="font-weight-500 mb-2 mt-0"
                  style={{ display: "inline-block" }}
                >
                  Data field which pinpoint locations on the map.
                </h5>
                <SelectPicker
                  preventOverflow
                  data={locationFields}
                  style={{ width: "100%" }}
                  labelKey="label"
                  valueKey="value"
                  placeholder="Select Field"
                  onChange={(newField) =>
                    updateSection("LocationField", newField)
                  }
                  value={widget?.LocationField}
                />
              </div>

              {/** Detail Window Fields section */}
              <div>
                <RequiredTitle
                  title="Detail Window Fields"
                  size="sm"
                  titleCSS="mb-0"
                />
                <h5
                  className="font-weight-500 mb-2 mt-0"
                  style={{ display: "inline-block" }}
                >
                  Metadata fields to display for enhanced pin information.
                </h5>
                <ModuleFieldsPicker
                  module={module}
                  availableFields={availableFields}
                  layoutLimit={4}
                  values={widget?.Query?.Fields ?? []}
                  onChange={(fields) =>
                    updateQueryFields(fields?.length ? fields : [])
                  }
                  setError={() => {}}
                  setValues={(fields) => {
                    updateQueryFields(fields?.length ? fields : []);
                  }}
                />
              </div>
            </>
          ) : (
            <NoDataSource />
          )}
        </>
      </SettingsGroup>
    </>
  );
}

function MapPinSizeDataSourceSettings({
  widget,
  updateSection,
  getModule,
}: {
  widget: any;
  updateSection: (propName: string, value: any) => void;
  getModule: (moduleAPIName: string) => Module | null;
}) {
  const updateField = (newField: string | null) => {
    let query = widget?.Query;

    if (!query) {
      query = {
        Fields: [],
        Filters: [],
        Lookup: [],
        Pagination: {
          Count: 100,
          Page: 1,
        },
      };
    }

    query.Lookup = [
      {
        ModuleAPIName: widget?.PinnedModuleAPIName,
        LocalField: "_id",
        ForeignField: newField,
        Operation: "count",
      },
    ];
    updateSection("Query", query);
  };

  const pinnedModule = useMemo(() => {
    if (!widget?.PinnedModuleAPIName) return null;

    return getModule(widget?.PinnedModuleAPIName);
  }, [widget?.PinnedModuleAPIName]);

  const relatedFields = useMemo(() => {
    if (!pinnedModule) return [];

    return pinnedModule?.Fields?.flatMap((f) => {
      if (
        ["SingleReference", "MultiReference"].includes(f.FieldType) &&
        f.SourcesList?.includes(`Tenant.Modules.${widget?.ModuleAPIName}`)
      )
        return [{ label: f.Name, value: f.APIName }];

      return [];
    });
  }, [pinnedModule, widget?.PinnedModuleAPIName]);

  return (
    <>
      <SettingsGroup
        title="Pin Source"
        className="mb-2"
        externalTrigger={null}
        closeExternalTrigger={null}
        disabledComponent={false}
      >
        <>
          {!widget?.ModuleAPIName ? (
            <NoDataSource />
          ) : (
            <>
              {/** Section to select the module to perform the lookup (JOIN) operation. */}
              <div className="pb-4">
                <h5 className="mb-0"> Pin Size</h5>
                <h5
                  className="font-weight-500 mb-2 mt-0"
                  style={{ display: "inline-block" }}
                >
                  Set pin sizes based on a data source.
                </h5>
                <ModulePicker
                  value={widget?.PinnedModuleAPIName}
                  onChange={(modAPINAme) =>
                    updateSection("PinnedModuleAPIName", modAPINAme)
                  }
                />
              </div>

              {/** Section to select the field to group the records. */}
              <div className="pb-4">
                <h5 className="mb-0"> Related By</h5>
                <h5
                  className="font-weight-500 mb-2 mt-0"
                  style={{ display: "inline-block" }}
                >
                  Field related to the origin module with the pin size one.
                </h5>
                <SelectPicker
                  preventOverflow
                  data={relatedFields}
                  style={{ width: "100%" }}
                  labelKey="label"
                  valueKey="value"
                  placeholder="Select Field"
                  onChange={updateField}
                  value={widget?.Query?.Lookup?.[0]?.ForeignField}
                />
              </div>
            </>
          )}
        </>
      </SettingsGroup>
    </>
  );
}

const NoDataSource = () => (
  <h5 className="mt-1 text-center">Select A Data Source</h5>
);

export default MapSettings;
